import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Smartphone, Monitor, Tablet } from 'lucide-react';
import { VideoFeatures } from '@/store/videoFeatures';
import { ResponsivePlayerConfig as ResponsivePlayerConfigType, DeviceType, DeviceRule, defaultResponsivePlayerConfig } from '@/store/videoFeatures/types/responsive-player';
import { DeviceTab } from './DeviceTab';
import { HeroCard } from '@/components/ui/HeroCard';
import { videoService } from '@/services/videoService';
import { useAuth } from '@/hooks/useAuth';

export interface LibraryVideo {
  id: string;
  title: string;
  thumbnailUrl: string;
}

interface ResponsivePlayerConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const ResponsivePlayerConfig: React.FC<ResponsivePlayerConfigProps> = ({
  features,
  setFeature
}) => {
  useLanguage();
  const { user } = useAuth();
  const config = features.responsivePlayer || defaultResponsivePlayerConfig;
  const [currentTab, setCurrentTab] = useState<DeviceType>('desktop');
  const [libraryVideos, setLibraryVideos] = useState<LibraryVideo[]>([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(true);

  // Busca os vídeos da biblioteca. Usa o userId do contexto de auth (não do
  // storage): com userId do storage ausente — caso em nova aba/refresh — e
  // deps [], a lista nunca carregava e o seletor ficava "sem vídeo". Depender
  // de user?.id faz o fetch disparar assim que o usuário fica disponível.
  useEffect(() => {
    const userId = user?.id || user?.id_user;
    if (!userId) return;
    setIsLoadingVideos(true);
    videoService.getDashboardVideos(userId)
      .then((fetched) => setLibraryVideos(fetched.map(v => ({
        id: v.id,
        title: v.title,
        thumbnailUrl: v.thumbnailUrl || '',
      }))))
      .catch(() => setLibraryVideos([]))
      .finally(() => setIsLoadingVideos(false));
  }, [user?.id, user?.id_user]);

  const updateConfig = (updates: Partial<ResponsivePlayerConfigType>) => {
    setFeature('responsivePlayer', { ...config, ...updates });
  };

  const updateDeviceRule = (device: DeviceType, updates: Partial<DeviceRule>) => {
    const rules = config.rules.map(rule => 
      rule.device === device ? { ...rule, ...updates } : rule
    );
    updateConfig({ rules });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Switch Mestre - HeroCard Premium */}
      <div className="px-6 pt-6 pb-4">
        <HeroCard
          icon={<Smartphone className="w-full h-full" strokeWidth={2} />}
          title={translateInterfaceText("Ativar Exibição por Dispositivo")}
          description={translateInterfaceText("Personalize o comportamento do player para cada tipo de dispositivo")}
          isActive={config.enabled}
          onToggle={(enabled) => updateConfig({ enabled })}
        />
      </div>

      {config.enabled ? (
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Tabs value={currentTab} onValueChange={(v) => setCurrentTab(v as DeviceType)} className="w-full">
            {/* Segmented Control Premium */}
            <TabsList className="w-full h-11 bg-[#F1F5F9] p-1 rounded-xl max-w-[480px]">
              <TabsTrigger 
                value="desktop" 
                className="flex-1 h-9 rounded-lg text-[13px] font-medium flex items-center justify-center gap-2 data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
              >
                <Monitor className="h-4 w-4" />
                <span>{translateInterfaceText("Desktop")}</span>
              </TabsTrigger>
              <TabsTrigger 
                value="tablet" 
                className="flex-1 h-9 rounded-lg text-[13px] font-medium flex items-center justify-center gap-2 data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
              >
                <Tablet className="h-4 w-4" />
                <span>{translateInterfaceText("Tablet")}</span>
              </TabsTrigger>
              <TabsTrigger 
                value="mobile" 
                className="flex-1 h-9 rounded-lg text-[13px] font-medium flex items-center justify-center gap-2 data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
              >
                <Smartphone className="h-4 w-4" />
                <span>{translateInterfaceText("Mobile")}</span>
              </TabsTrigger>
            </TabsList>

            <div className="mt-3">
              <p className="text-[11px] text-[#6B7280]">
                {translateInterfaceText("As configurações são salvas individualmente por dispositivo.")}</p>
            </div>

            <TabsContent value="desktop" className="mt-6">
              <DeviceTab
                device="desktop"
                rule={config.rules.find(r => r.device === 'desktop')!}
                onUpdate={(updates) => updateDeviceRule('desktop', updates)}
                libraryVideos={libraryVideos}
                isLoading={isLoadingVideos}
              />
            </TabsContent>

            <TabsContent value="tablet" className="mt-6">
              <DeviceTab
                device="tablet"
                rule={config.rules.find(r => r.device === 'tablet')!}
                onUpdate={(updates) => updateDeviceRule('tablet', updates)}
                libraryVideos={libraryVideos}
                isLoading={isLoadingVideos}
              />
            </TabsContent>

            <TabsContent value="mobile" className="mt-6">
              <DeviceTab
                device="mobile"
                rule={config.rules.find(r => r.device === 'mobile')!}
                onUpdate={(updates) => updateDeviceRule('mobile', updates)}
                libraryVideos={libraryVideos}
                isLoading={isLoadingVideos}
              />
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <div className="px-6 pb-6">
          <div className="mt-6 rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 p-12 text-center max-w-[480px]">
            <Monitor className="mx-auto h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("Ative a exibição por dispositivo para personalizar Desktop, Tablet e Mobile.")}</p>
          </div>
        </div>
      )}
    </div>
  );
};
